import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormLabel, Grid, LinearProgress, Paper, Radio, RadioGroup, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@material-ui/core"
import Typography from "@material-ui/core/Typography"
import React from "react";
import { useAssetsCalibrationListView, useAssetsCalibrationMail } from "../../../intake/intakeQueries";
import { useFetchContext } from "../../../common/FetchContext";
import { toast } from "react-toastify";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { generalFilter, useDefaultColumn } from "../../../../util/filter";
import { HeaderProps, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { renderHeader } from "../../../common/util";
import MuiTable from "@material-ui/core/Table";
import { formatDate } from "../../../common/formatDate";
import { Autocomplete } from "@material-ui/lab";
import { useUsers } from "../../userQueries";
import ScreenWidth from "../../../common/ScreenWidth";


export const AssetCalibrationListTable = () => {

    const { authAxios } = useFetchContext();

    const [startDate, setStartDate] = React.useState('');
    const [filterType, setFilterType] = React.useState<"all" | "date">("all");
    const [OpenSendMailDialog, setOpenSendMailDialog] = React.useState(false);
    const [selectedRecipients, setSelectedRecipients] = React.useState<any[]>([]);

    const [CalibrationData, setCalibrationData] = React.useState([]);
    const defaultColumn = useDefaultColumn();
    const widthCurrent = ScreenWidth();

    const {
        mutateAsync: mutateGetAssetCalibrationListView,
        error: errorMutateAssetCalibrationListView,
        isError: isErrorMutateAssetCalibrationListView,
        isSuccess: isSuccessAssetCalibrationListView,
        isLoading: isLoadingAssetCalibrationListView,
    } = useAssetsCalibrationListView();

    const {
        data: users,
        isError: isErrorUsers,
        isLoading: isLoadingUsers,
        error: errorUsers,
    } = useUsers();

    const {
        mutateAsync: mutateSendAssetCalibrationMail,
        isLoading: isLoadingSendMail
    } = useAssetsCalibrationMail();


    const handleFilterChange = (value: "all" | "date") => {
        setFilterType(value);

        if (value === "all") {
            setStartDate(""); // clear date
        }
    };

    const handleStartDateChange = (e: any) => {
        setStartDate(e.target.value);
        setFilterType("date"); // auto switch
    };

    const searchData = async () => {

        let payload: any = {
            authAxios,
            all: filterType === "all",
            date: ""

        };

        if (filterType === "date") {
            if (!startDate) {
                toast.warning('Please select date');
                return;
            }
            payload.date = startDate;
        }

        const AssetData = await mutateGetAssetCalibrationListView(payload);
        setCalibrationData(AssetData.data);
    };

    const sendMail = async () => {
        let payload: any = {
            authAxios,
            all: filterType === "all",
            date: "",

            emails: JSON.stringify(
                selectedRecipients.map((recipient) => recipient.email)
            ),
        };

        if (filterType === "date") {
            if (!startDate) {
                toast.warning("Please select date");
                return;
            }
            payload.date = startDate;
        }

        try {
            await mutateSendAssetCalibrationMail(payload);
            console.log("payload", payload);
            toast.success("Mail sent successfully");
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Failed to send mail");
            console.log("payload", payload);

        }
    };



    const columns: any[] = React.useMemo(
        () => [

            {
                Header: "ID",
                id: 'id',
                accessor: "id",
                filter: generalFilter,
            },
            {
                id: "old_data",
                accessor: 'old_correction_value'
            },

            {
                id: "Asset ID",
                accessor: "asset_id",
                minWidth: 200,
                width: 300,
                maxWidth: 400,
                filter: generalFilter,
                Cell: ({ cell }: any) => (
                    <Typography>
                        {cell.value ? cell.value : "N/A"}
                    </Typography>
                ),

            },
            {
                id: "Asset Name",
                accessor: "asset_name",
                filter: generalFilter,
                minWidth: 200,
                width: 300,
                maxWidth: 400,
                Cell: ({ cell }: any) => (
                    <Typography>
                        {cell.value ? cell.value : "N/A"}
                    </Typography>
                ),

            },
            {
                id: "Asset Type",
                accessor: "asset_type",
                filter: generalFilter,
                minWidth: 200,
                width: 300,
                maxWidth: 400,
                Cell: ({ cell }: any) => (
                    <Typography>
                        {cell.value ? cell.value : "N/A"}
                    </Typography>
                ),
            },
            {
                id: "Last Calibrated Date",
                accessor: "last_calibrated_date",
                minWidth: 200,
                width: 300,
                maxWidth: 400,
                Cell: ({ cell, row }: any) => {
                    return cell.value ? formatDate(cell.value) : "N/A";
                }
            },
            {
                id: "Days Remaining",
                accessor: "schedule_for_calibration",
                filter: generalFilter,
                minWidth: 200,
                width: 300,
                maxWidth: 400,
                Cell: ({ cell }: any) => (
                    <Typography>
                        {cell.value ? cell.value : "N/A"}
                    </Typography>
                ),
            },
            {
                id: "Calibration Due Date",
                accessor: "calibration_due_date",
                minWidth: 200,
                width: 300,
                maxWidth: 400,
                Cell: ({ cell, row }: any) => {
                    return cell.value ? formatDate(cell.value) : "N/A";
                }
            },

        ],
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,


    } = useTable(
        {
            data: CalibrationData,
            columns,
            defaultColumn,
            initialState: {
                hiddenColumns: ["id", "url", "old_data"],
            },
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    );


    if (isLoadingAssetCalibrationListView) {
        return <LinearProgress />;
    }

    if (isErrorMutateAssetCalibrationListView) {
        return <ErrorMessage error={errorMutateAssetCalibrationListView} />;
    }

    return (

        <>
            <Typography variant="h4">Monthly Asset Calibration Report</Typography>

            <div>

                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between" // Ensure items are spread across the row
                >


                    <Grid item xs={6}
                        container
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                        style={{ marginTop: 20 }}>

                        <Grid item >

                            <FormControl component="fieldset">
                                <FormLabel component="legend">Filter by status</FormLabel>
                                <RadioGroup
                                    row
                                    value={filterType}
                                    onChange={(e) => handleFilterChange(e.target.value as "all" | "date")}
                                >
                                    <FormControlLabel value="all" control={<Radio />} label="All" />
                                    <FormControlLabel value="date" control={<Radio />} label="Date" />
                                </RadioGroup>
                            </FormControl>

                        </Grid>

                        <Grid item >
                            <TextField
                                id="start-date"
                                label="Start Date"
                                type="month"
                                value={startDate}
                                onChange={handleStartDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                variant="outlined"
                                disabled={filterType === "all"} // ✅ THIS LINE
                            />
                        </Grid>



                        <Grid item>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={searchData}
                            >
                                SEARCH
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid item xs={6}
                        container
                        justifyContent="flex-end"
                        alignItems="center"
                        spacing={2}
                        style={{ marginTop: 20, }}>

                        <Grid item>
                            {CalibrationData?.length > 0 && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    // onClick={sendMail}
                                    onClick={() => setOpenSendMailDialog(true)}

                                    style={{ marginRight: 120 }}
                                >
                                    SEND MAIL
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Grid>


            </div>

            <div>
                {CalibrationData?.length ?

                    <TableContainer
                        data-testid="tableContainer"
                        // style={theme.tableContainer}
                        style={{
                            marginTop: 20, maxHeight: "500px",
                            overflowX: 'auto',
                            overflowY: 'auto',
                            whiteSpace: 'nowrap',
                            maxWidth: `${widthCurrent}px`
                        }}

                        component={Paper}
                    >
                        <MuiTable stickyHeader {...getTableProps()} size="small">
                            <TableHead>
                                {headerGroups.map((headerGroup) => (
                                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <TableCell style={{
                                                whiteSpace: 'nowrap', width: column.width,
                                                minWidth: column.minWidth,
                                                maxWidth: column.maxWidth,
                                                textAlign: 'center',

                                            }}>
                                                <Grid
                                                    container
                                                    direction="row"
                                                    alignItems="center"
                                                    wrap="nowrap"
                                                    spacing={0}
                                                >
                                                    <Grid item {...column.getHeaderProps()}>
                                                        <Grid
                                                            container
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={0}
                                                            wrap="nowrap"
                                                        >
                                                            <Typography variant="subtitle1" noWrap>
                                                                {renderHeader(column)}
                                                            </Typography>
                                                            {column.canFilter ? column.render("Filter") : null}
                                                            <Grid item wrap="nowrap">

                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody {...getTableBodyProps()}>
                                {rows.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <TableRow {...row.getRowProps()}>
                                            {row.cells.map((cell) => {
                                                return (
                                                    <TableCell {...cell.getCellProps()} style={{ whiteSpace: 'nowrap' }}>
                                                        {cell.render("Cell")}

                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </MuiTable>
                    </TableContainer>
                    :
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                        <Typography variant="h5">No result found</Typography>
                    </div>
                }
            </div>

            <Dialog
                open={OpenSendMailDialog}
                onClose={() => {
                    setOpenSendMailDialog(false);
                    setSelectedRecipients([]);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Select Recipients</DialogTitle>
                <Divider />
                <DialogContent>

                    <Autocomplete
                        multiple
                        options={users || []}
                        getOptionLabel={(option) =>
                            `${option.first_name} ${option.last_name} (${option.email})`
                        }
                        value={selectedRecipients}
                        onChange={(event, newValue) => setSelectedRecipients(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Select Recipients"
                                placeholder="Choose emails"
                            />
                        )}
                    />

                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenSendMailDialog(false)} color="secondary">
                        Cancel
                    </Button>

                    <Button
                        onClick={() => {
                            sendMail();
                            setSelectedRecipients([]);
                            setOpenSendMailDialog(false);

                        }}
                        color="primary"

                        disabled={selectedRecipients.length === 0 || isLoadingSendMail}
                    >
                        Send Mail
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )


};



